import type { NastavitvePartnerjev as Nastavitve, StranPrometa } from '@koda/shared';
import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import { Link, useParams } from 'react-router-dom';

import { api } from '../lib/api';
import { znesek } from '../lib/format';
import { useBesedila, useJezik } from '../lib/jezik';
import { razcleniKonte } from '../sestavni/FilterPartnerjev';

/** Vrstica urejevalnika; konti so besedilo, dokler se ne shranijo. */
interface Osnutek {
  kljuc: string;
  naziv: string;
  konti: string;
  stran: StranPrometa;
  privzeta: boolean;
}

let zaporedna = 0;
const novKljuc = (): string => `nova-${String(++zaporedna)}`;

/**
 * Nastavitve zavihkov Partnerji in RVC po kupcih (docs/ARHITEKTURA.md, 6.5).
 *
 * Prednastavitve zavihka Partnerji (naziv, konti, stran, privzeto odprta) in
 * vrste dokumentov za RVC po kupcih. Bere jih vsak uporabnik podjetja, ureja
 * pa poleg servisa tudi skrbnik stranke – odločitev lastnika: kateri konti so
 * »promet partnerja«, je stvar stranke. Kdo sme urejati, pove strežnik
 * (`smemUrejati`); stran z vpogledom samo pokaže, kaj velja.
 */
export function NastavitvePartnerjev(): JSX.Element {
  const { id = '' } = useParams();
  const b = useBesedila();
  const jezik = useJezik();
  const [nazivPodjetja, setNazivPodjetja] = useState('');
  const [nastavitve, setNastavitve] = useState<Nastavitve | null>(null);
  const [osnutki, setOsnutki] = useState<Osnutek[]>([]);
  const [vrste, setVrste] = useState('');
  /**
   * Katere vrste štejejo v RVC: vse (prazna nastavitev v bazi) ali samo
   * izbrane. Ločena izbira, ker prazen seznam pomeni »vsi dokumenti« – brez
   * nje bi odkljukana zadnja vrsta tiho vrnila tudi nabave (npr. 150, 430),
   * kljukice pa bi kazale nasprotno od izračuna.
   */
  const [samoIzbrane, setSamoIzbrane] = useState(false);
  const [napaka, setNapaka] = useState<string | null>(null);
  /** Vrstice prednastavitev z napačnimi ali manjkajočimi konti; polje je označeno. */
  const [napacneVrstice, setNapacneVrstice] = useState<Set<string>>(new Set());
  const [sporocilo, setSporocilo] = useState<string | null>(null);
  const [shranjujem, setShranjujem] = useState(false);

  /**
   * Zastareli odgovori se zavržejo: prikaže se samo zadnje branje.
   *
   * `del` pove, kateri razdelek naj se napolni iz baze. Po shranjevanju enega
   * razdelka se drugi ne povozi: njegove neshranjene spremembe ostanejo.
   */
  const zadnjeBranje = useRef(0);
  const osvezi = useCallback((del: 'vse' | 'prednastavitve' | 'vrste' = 'vse'): Promise<void> => {
    const branje = ++zadnjeBranje.current;
    return api
      .nastavitvePartnerjev(id)
      .then((n) => {
        if (branje !== zadnjeBranje.current) return;
        setNastavitve(n);
        if (del !== 'vrste') {
          setOsnutki(
            n.prednastavitve.map((p) => ({
              kljuc: p.koda,
              naziv: p.naziv,
              konti: p.predpone.join(', '),
              stran: p.stran,
              privzeta: p.privzeta,
            })),
          );
          setNapacneVrstice(new Set());
        }
        if (del !== 'prednastavitve') {
          setVrste(n.vrsteRvc.join(', '));
          setSamoIzbrane(n.vrsteRvc.length > 0);
        }
      })
      .catch((e: unknown) => {
        if (branje === zadnjeBranje.current) setNapaka(e instanceof Error ? e.message : b.pa.napakaBranja);
      });
  }, [id, b]);

  useEffect(() => {
    setNastavitve(null);
    setNapaka(null);
    setSporocilo(null);
    void osvezi();
    return () => {
      zadnjeBranje.current += 1;
    };
  }, [osvezi, jezik]);

  useEffect(() => {
    let velja = true;
    setNazivPodjetja('');
    api
      .podjetja()
      .then((vsa) => {
        if (velja) setNazivPodjetja(vsa.find((p) => p.id === id)?.naziv ?? '');
      })
      .catch(() => {
        /* naziv ni nujen za delovanje strani */
      });
    return () => {
      velja = false;
    };
  }, [id]);

  const smemUrejati = nastavitve?.smemUrejati === true;

  /**
   * Shrani in prebere znova samo shranjeni razdelek; izbire so zaklenjene,
   * dokler novo stanje ni na zaslonu.
   */
  const shrani = (delo: Promise<unknown>, del: 'prednastavitve' | 'vrste'): void => {
    setShranjujem(true);
    setNapaka(null);
    setSporocilo(null);
    delo
      .then(() => osvezi(del))
      .then(() => {
        setSporocilo(b.pa.shranjeno);
      })
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : b.pa.napakaShranjevanja);
      })
      .finally(() => {
        setShranjujem(false);
      });
  };

  const spremeni = (kljuc: string, sprememba: Partial<Osnutek>): void => {
    setOsnutki(osnutki.map((o) => (o.kljuc === kljuc ? { ...o, ...sprememba } : o)));
    if (sprememba.konti !== undefined && napacneVrstice.has(kljuc)) {
      const nove = new Set(napacneVrstice);
      nove.delete(kljuc);
      setNapacneVrstice(nove);
    }
  };

  /**
   * Shrani prednastavitve. Napačen konto (npr. »23x«) ali vrstica brez kontov
   * ustavi shranjevanje: vrstica je označena, sporočilo pove, kaj popraviti.
   * Tiho izpuščen konto bi shranil nastavitev z manj konti, kot jih je
   * uporabnik vnesel.
   */
  const shraniPrednastavitve = (): void => {
    const razclenjene = osnutki.map((o) => ({ o, ...razcleniKonte(o.konti) }));
    const napake: string[] = [];
    const napacne = new Set<string>();
    razclenjene.forEach(({ o, konti, napacni }, i) => {
      const ime = o.naziv.trim() === '' ? String(i + 1) : o.naziv.trim();
      if (napacni.length > 0) napake.push(b.pa.napakaVrstice(ime, b.pa.napacniKonti(napacni.join(', '))));
      else if (konti.length === 0) napake.push(b.pa.napakaVrstice(ime, b.pa.vnesiKonto));
      else return;
      napacne.add(o.kljuc);
    });
    setNapacneVrstice(napacne);
    if (napake.length > 0) {
      setSporocilo(null);
      setNapaka(napake.join(' '));
      return;
    }
    shrani(
      api.shraniPrednastavitvePartnerjev(
        id,
        razclenjene.map(({ o, konti }) => ({
          naziv: o.naziv.trim(),
          predpone: konti,
          stran: o.stran,
          privzeta: o.privzeta,
        })),
      ),
      'prednastavitve',
    );
  };

  /**
   * Shrani vrste. »Vsi dokumenti« shrani prazen seznam; »Samo izbrane vrste«
   * brez izbrane vrste se ne shrani, ker bi prazen seznam pomenil vse.
   */
  const shraniVrste = (): void => {
    if (samoIzbrane && izbraneVrste.length === 0) {
      setSporocilo(null);
      setNapaka(b.pa.izberiVrsto);
      return;
    }
    shrani(api.shraniVrsteRvc(id, samoIzbrane ? izbraneVrste : []), 'vrste');
  };

  /** Vrste iz vnosa (brez dvojnikov), da jih kljukice in vnos delijo. */
  const izbraneVrste = [
    ...new Set(
      vrste
        .split(/[\s,;]+/)
        .map((v) => v.trim())
        .filter((v) => v !== ''),
    ),
  ];
  const preklopiVrsto = (vrsta: string, vkljuci: boolean): void => {
    const nove = vkljuci ? [...izbraneVrste, vrsta] : izbraneVrste.filter((v) => v !== vrsta);
    setVrste(nove.join(', '));
  };

  const konti = nastavitve?.kontiRvc;
  const brezKontov = konti !== undefined && konti.prodajna.length === 0 && konti.nabavna.length === 0;

  return (
    <main className="siroko">
      <div className="vsebina">
        <nav className="drobtine">
          <Link to="/">{b.podjetja}</Link>
          <Link to={`/podjetje/${id}`}>{nazivPodjetja || b.podjetje}</Link>
        </nav>

        <h1>{b.pa.naslov}</h1>
        <p className="podnaslov">{b.pa.podnaslov}</p>

        {napaka !== null && <p className="napaka">{napaka}</p>}
        {sporocilo !== null && <p className="uspeh-besedilo">{sporocilo}</p>}
        {nastavitve === null && napaka === null && <p>{b.sn.beremNastavitve}</p>}

        {nastavitve !== null && (
          <>
            {!smemUrejati && <p className="namig">{b.pa.samoBranje}</p>}

            <section className="kartica" aria-label={b.pa.prednastavitve}>
              <h2>{b.pa.prednastavitve}</h2>
              <p className="namig">{nastavitve.standardne ? b.pa.standardne : b.pa.lastne}</p>
              <table className="nastavitve prednastavitve">
                <thead>
                  <tr>
                    <th>{b.pa.naziv}</th>
                    <th>{b.pa.konti}</th>
                    <th>{b.pa.stran}</th>
                    <th title={b.pa.namigPrivzeta}>{b.pa.privzeta}</th>
                    {smemUrejati && <th className="ozek" />}
                  </tr>
                </thead>
                <tbody>
                  {osnutki.map((o, i) => (
                    <tr key={o.kljuc}>
                      <td>
                        <input
                          type="text"
                          aria-label={`${b.pa.naziv} ${String(i + 1)}`}
                          value={o.naziv}
                          maxLength={80}
                          disabled={!smemUrejati || shranjujem}
                          onChange={(d) => {
                            spremeni(o.kljuc, { naziv: d.target.value });
                          }}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          aria-label={`${b.pa.konti} ${String(i + 1)}`}
                          value={o.konti}
                          placeholder="22, 23"
                          aria-invalid={napacneVrstice.has(o.kljuc)}
                          className={napacneVrstice.has(o.kljuc) ? 'napacen' : undefined}
                          disabled={!smemUrejati || shranjujem}
                          onChange={(d) => {
                            spremeni(o.kljuc, { konti: d.target.value });
                          }}
                        />
                      </td>
                      <td>
                        <select
                          aria-label={`${b.pa.stran} ${String(i + 1)}`}
                          value={o.stran}
                          disabled={!smemUrejati || shranjujem}
                          onChange={(d) => {
                            spremeni(o.kljuc, { stran: d.target.value === 'breme' ? 'breme' : 'dobro' });
                          }}
                        >
                          <option value="dobro">{b.pa.strani.dobro}</option>
                          <option value="breme">{b.pa.strani.breme}</option>
                        </select>
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`${b.pa.privzeta} ${String(i + 1)}`}
                          checked={o.privzeta}
                          disabled={!smemUrejati || shranjujem}
                          onChange={(d) => {
                            spremeni(o.kljuc, { privzeta: d.target.checked });
                          }}
                        />
                      </td>
                      {smemUrejati && (
                        <td className="ozek">
                          <button
                            type="button"
                            className="povezava-gumb"
                            disabled={shranjujem}
                            onClick={() => {
                              setOsnutki(osnutki.filter((x) => x.kljuc !== o.kljuc));
                            }}
                          >
                            {b.pa.odstrani}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              {smemUrejati && (
                <p className="gumbi-nastavitev">
                  <button
                    type="button"
                    disabled={shranjujem || osnutki.length >= 20}
                    onClick={() => {
                      setOsnutki([...osnutki, { kljuc: novKljuc(), naziv: '', konti: '', stran: 'dobro', privzeta: false }]);
                    }}
                  >
                    {b.pa.dodaj}
                  </button>{' '}
                  <button type="button" className="gumb" disabled={shranjujem} onClick={shraniPrednastavitve}>
                    {b.pa.shrani}
                  </button>{' '}
                  {!nastavitve.standardne && (
                    <button
                      type="button"
                      disabled={shranjujem}
                      onClick={() => {
                        shrani(api.shraniPrednastavitvePartnerjev(id, []), 'prednastavitve');
                      }}
                    >
                      {b.pa.vrniPrivzete}
                    </button>
                  )}
                </p>
              )}
            </section>

            <section className="kartica" aria-label={b.pa.vrsteNaslov}>
              <h2>{b.pa.vrsteNaslov}</h2>
              <p className="namig">{b.pa.vrsteNamig}</p>
              <p className="namig">
                {brezKontov
                  ? b.pa.brezKontovRvc
                  : b.pa.kontiRvc(konti?.prodajna.join(', ') ?? '', konti?.nabavna.join(', ') ?? '')}
              </p>
              {!brezKontov &&
                (nastavitve.vrsteNaKontih.length === 0 ? (
                  <p className="namig">{b.pa.niVrstNaKontih}</p>
                ) : (
                  <>
                    <fieldset className="nacin-vrst" disabled={!smemUrejati || shranjujem}>
                      <legend>{b.pa.nacinVrst}</legend>
                      <label>
                        <input
                          type="radio"
                          name="nacin-vrst"
                          checked={!samoIzbrane}
                          onChange={() => {
                            setSamoIzbrane(false);
                          }}
                        />{' '}
                        {b.pa.vsiDokumenti}
                      </label>
                      <label>
                        <input
                          type="radio"
                          name="nacin-vrst"
                          checked={samoIzbrane}
                          onChange={() => {
                            setSamoIzbrane(true);
                          }}
                        />{' '}
                        {b.pa.samoIzbrane}
                      </label>
                    </fieldset>
                    <p className="namig">{b.pa.vrsteNaKontihNamig}</p>
                    <table className="nastavitve vrste-rvc">
                      <thead>
                        <tr>
                          <th>{b.pa.stejeVRvc}</th>
                          <th>{b.pa.vrstaDokumenta}</th>
                          <th className="stevilka">{b.pa.knjizb}</th>
                          <th className="stevilka">{b.pa.prodajnaVrednost}</th>
                          <th className="stevilka">{b.pa.nabavnaVrednost}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {nastavitve.vrsteNaKontih.map((v) => (
                          <tr key={v.vrsta}>
                            <td>
                              <input
                                type="checkbox"
                                aria-label={`${b.pa.stejeVRvc} ${v.vrsta === '' ? b.pa.brezVrste : v.vrsta}`}
                                // Pri »Vsi dokumenti« šteje vse – kljukice to pokažejo
                                // in so zaklenjene. Knjižb brez vrste filter ne more izbrati.
                                disabled={!smemUrejati || shranjujem || !samoIzbrane || v.vrsta === ''}
                                checked={!samoIzbrane || (v.vrsta !== '' && izbraneVrste.includes(v.vrsta))}
                                onChange={(d) => {
                                  preklopiVrsto(v.vrsta, d.target.checked);
                                }}
                              />
                            </td>
                            <td>{v.vrsta === '' ? b.pa.brezVrste : <code>{v.vrsta}</code>}</td>
                            <td className="stevilka">{String(v.knjizb)}</td>
                            <td className="stevilka">{znesek(v.prodajna)}</td>
                            <td className="stevilka">{znesek(v.nabavna)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                ))}
              <p className="vnos-vrst">
                <label>
                  {b.pa.vrste}{' '}
                  <input
                    type="text"
                    value={vrste}
                    placeholder="420, 481"
                    disabled={!smemUrejati || shranjujem || !samoIzbrane}
                    onChange={(d) => {
                      setVrste(d.target.value);
                    }}
                  />
                </label>{' '}
                {smemUrejati && (
                  <button type="button" className="gumb" disabled={shranjujem} onClick={shraniVrste}>
                    {b.pa.shrani}
                  </button>
                )}
              </p>
            </section>
          </>
        )}
      </div>
    </main>
  );
}
